// Mobile-only appointment calendar surface. Replaces the desktop's
// agenda/week-grid combo with a focused single-day list anchored on a
// horizontal week strip, matching the design.
//
// Tracks design source: user-supplied design comp showing
// "Lịch hẹn / Tháng 5 · 2026" with a Ngày/Tuần toggle, a horizontal
// week strip, and per-appointment cards with a time column on the left.
//
// Composition (top to bottom):
//   - Title block: "Lịch hẹn" + "Tháng N · YYYY" subtitle, with a
//     Ngày/Tuần toggle pill on the right.
//   - Date strip: 7 day-cells showing the calendar week containing
//     the anchor date. Each cell shows the weekday short label and
//     the date; the anchor cell is filled black, cells with any
//     appointments get a small indigo dot under the date.
//   - Day/week summary: "Hôm nay · {N} lịch" (or weekday + date)
//     plus the start-to-end time range of the day's appointments.
//   - Card list: one MobileAppointmentCard per appointment in the
//     selected day (Ngày) or grouped by day (Tuần).
//   - Live "ĐANG" indicator on the card whose scheduledAt..end window
//     contains the current minute. Recomputed every minute via the
//     same lazy-init useState + setInterval pattern as the lead
//     follow-up countdown.

import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  addDays,
  dateKey,
  endOfDay,
  formatTime,
  isSameDay,
  isToday,
  startOfDay,
  startOfWeek,
} from "@/lib/date";
import { useSwipeNav } from "@/features/property/wizard/use-swipe-nav";
import { NotificationBell } from "@/features/notification/notification-bell";
import { APPOINTMENT_STATUS_LABELS } from "@/features/appointment/constants";
import type {
  Appointment,
  AppointmentStatus,
} from "@/features/appointment/types";
import type { Lead } from "@/features/lead/types";
import type { BrokerSummary } from "@/features/appointment/types";
import { NOMINAL_VIEWING_MINUTES } from "@/features/appointment/types";

interface AppointmentMobileViewProps {
  view: "day" | "week";
  anchorDate: Date;
  appointments: Appointment[];
  leads: Lead[];
  brokers: BrokerSummary[];
  onViewChange: (next: "day" | "week") => void;
  onDateChange: (next: Date) => void;
  onSelectAppointment: (appointment: Appointment) => void;
}

const VN_WEEKDAY_SHORT = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"] as const;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// Mobile-card pill tones. Slightly more saturated than the desktop
// badge palette since the chip is the only colour on the row.
const STATUS_PILL_TONE: Record<AppointmentStatus, string> = {
  PENDING_SOURCE: "border-amber-200 bg-amber-50 text-amber-700",
  PENDING_AGENT: "border-violet-200 bg-violet-50 text-violet-700",
  SCHEDULED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  COMPLETED: "border-zinc-200 bg-zinc-50 text-zinc-600",
  CANCELED: "border-zinc-200 bg-zinc-100 text-zinc-500 line-through",
  NO_SHOW: "border-slate-200 bg-slate-50 text-slate-600",
};

const STATUS_DOT_TONE: Record<AppointmentStatus, string> = {
  PENDING_SOURCE: "bg-amber-500",
  PENDING_AGENT: "bg-violet-500",
  SCHEDULED: "bg-emerald-500",
  COMPLETED: "bg-zinc-400",
  CANCELED: "bg-zinc-400",
  NO_SHOW: "bg-slate-400",
};

// Design uses softer copy than our backend labels — "Xác nhận" reads
// like "confirmed for the day" while "Đã lên lịch" feels distant. We
// keep the wire label intact and remap at the surface.
const STATUS_LABEL_MOBILE: Record<AppointmentStatus, string> = {
  PENDING_SOURCE: "Chờ nguồn",
  PENDING_AGENT: "Chờ khách",
  SCHEDULED: "Xác nhận",
  COMPLETED: "Hoàn thành",
  CANCELED: "Đã huỷ",
  NO_SHOW: "Không đến",
};

export function AppointmentMobileView({
  view,
  anchorDate,
  appointments,
  leads,
  brokers,
  onViewChange,
  onDateChange,
  onSelectAppointment,
}: AppointmentMobileViewProps) {
  // "Now" lives in state so the live "ĐANG" highlight + relative
  // wording stay correct without a Date.now() call in render (React
  // Compiler purity). Refreshes every minute, the same cadence we use
  // for the lead follow-up card.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(handle);
  }, []);

  // Compute the calendar-week the anchor sits in. weekStartsOn: 1 →
  // Monday-first, matching the desktop view and Vietnamese convention.
  const weekStart = startOfWeek(anchorDate, { weekStartsOn: 1 });
  const weekDays: Date[] = [];
  for (let i = 0; i < 7; i++) weekDays.push(addDays(weekStart, i));

  // Map every day in the week (and the anchor's day specifically) to its
  // appointment list, sorted by scheduledAt. Done in one pass so the
  // date strip's "has appointments" dot and the body list both read
  // from the same partition.
  const byDate = new Map<string, Appointment[]>();
  for (const appt of appointments) {
    const key = dateKey(appt.scheduledAt);
    const bucket = byDate.get(key);
    if (bucket === undefined) byDate.set(key, [appt]);
    else bucket.push(appt);
  }
  for (const bucket of byDate.values()) {
    bucket.sort(
      (a, b) =>
        new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
    );
  }

  const month = anchorDate.getMonth() + 1;
  const year = anchorDate.getFullYear();

  // Swipe nav on the date strip. Swipe-left → next week, swipe-right →
  // prev week. The default 60px threshold + 30° angle tolerance from
  // the hook is enough to distinguish from accidental drag while
  // tapping a cell. We also set `touch-action: pan-y` on the strip so
  // the browser still owns vertical scrolling.
  const swipeHandlers = useSwipeNav({
    onSwipeLeft: () => onDateChange(addDays(anchorDate, 7)),
    onSwipeRight: () => onDateChange(addDays(anchorDate, -7)),
  });

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-24">
      {/* Title + Ngày/Tuần toggle. The subtitle row carries small
          prev/next chevrons so brokers can step a week without
          horizontal scrolling — the previous "scroll the strip"
          approach hid this affordance and overflowed on narrow phones. */}
      <header className="flex flex-col gap-1.5">
        {/* Row 1: title + bell + view toggle. Row 2: month + "Hôm nay",
            full-width so it never wraps when the control cluster is wide. */}
        <div className="flex items-center justify-between gap-3">
          <h1 className="min-w-0 truncate text-[28px] font-bold leading-tight tracking-tight">
            Lịch hẹn
          </h1>
          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell />
            <div className="flex shrink-0 items-center gap-1 rounded-xl border border-border bg-card p-1">
              <ToggleButton
                label="Ngày"
                active={view === "day"}
                onClick={() => onViewChange("day")}
              />
              <ToggleButton
                label="Tuần"
                active={view === "week"}
                onClick={() => onViewChange("week")}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-mono text-[12px] text-muted-foreground">
            Tháng {month} · {year}
          </p>
          {/* Jump-to-today affordance. Sized so "Hôm nay" sits with
              comfortable padding — the earlier h-6/px-2 cramped the
              word against the borders. h-7 + px-3 gives a ~76px-
              wide pill which reads as a clearly-labelled button. */}
          <button
            type="button"
            onClick={() => onDateChange(new Date())}
            className="inline-flex h-7 items-center rounded-md border border-border bg-card px-3 text-[12px] font-semibold text-foreground hover:bg-muted"
          >
            Hôm nay
          </button>
        </div>
      </header>

      {/* Date strip — 7 day-cells in a fixed 7-column grid so they
          share the page width without horizontal scrolling. The
          previous flex+w-14 layout overflowed at ≤414px because
          7×56 + gaps already exceeds the content area.
          `touch-action: pan-y` reserves horizontal pointer gestures
          for `useSwipeNav` (week change) while letting the browser
          keep ownership of vertical scrolling on the strip. */}
      <div
        role="tablist"
        aria-label="Ngày trong tuần"
        className="grid touch-pan-y grid-cols-7 gap-1.5"
        {...swipeHandlers}
      >
        {weekDays.map((day) => {
          const active = isSameDay(day, anchorDate);
          const hasAppointments = (byDate.get(dateKey(day)) ?? []).length > 0;
          return (
            <button
              key={day.toISOString()}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onDateChange(day)}
              className={cn(
                "relative flex h-14 flex-col items-center justify-center gap-0.5 rounded-xl border transition-colors",
                active
                  ? "border-foreground bg-foreground text-background shadow-[var(--shadow-soft)]"
                  : "border-border bg-card text-foreground hover:bg-muted",
              )}
            >
              <span
                className={cn(
                  "font-mono text-[11px] tracking-wider",
                  active ? "text-background/80" : "text-muted-foreground",
                )}
              >
                {VN_WEEKDAY_SHORT[day.getDay()]}
              </span>
              <span className="text-[20px] font-bold leading-none">
                {day.getDate()}
              </span>
              {/* Small dot beneath the date indicates "this day has
                  appointments". Recoloured to soft white when the cell
                  is the active black tile so it stays legible. */}
              {hasAppointments ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute bottom-1.5 h-1 w-1 rounded-full",
                    active ? "bg-background/70" : "bg-primary",
                  )}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Body — Ngày shows the anchor's appointments; Tuần shows the
          whole week grouped by day. Each branch builds its own summary
          line so the wording is honest about what's on screen. */}
      {view === "day" ? (
        <DayView
          anchorDate={anchorDate}
          appointments={byDate.get(dateKey(anchorDate)) ?? []}
          now={now}
          leads={leads}
          brokers={brokers}
          onSelectAppointment={onSelectAppointment}
        />
      ) : (
        <WeekView
          weekDays={weekDays}
          byDate={byDate}
          now={now}
          leads={leads}
          brokers={brokers}
          onDateChange={onDateChange}
          onSelectAppointment={onSelectAppointment}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

interface DayViewProps {
  anchorDate: Date;
  appointments: Appointment[];
  now: number;
  leads: Lead[];
  brokers: BrokerSummary[];
  onSelectAppointment: (appointment: Appointment) => void;
}

function DayView({
  anchorDate,
  appointments,
  now,
  leads,
  brokers,
  onSelectAppointment,
}: DayViewProps) {
  const summaryLabel = isToday(anchorDate)
    ? "Hôm nay"
    : `${["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"][anchorDate.getDay()]}, ${pad2(anchorDate.getDate())}/${pad2(anchorDate.getMonth() + 1)}`;

  const dayStart = startOfDay(anchorDate).getTime();
  const dayEnd = endOfDay(anchorDate).getTime();
  // Only count today's appointments — the parent might pass a wider
  // range if Tuần was the prior view; defending the count here keeps
  // the summary honest.
  const todays = appointments.filter((a) => {
    const t = new Date(a.scheduledAt).getTime();
    return t >= dayStart && t <= dayEnd;
  });

  // Range readout — earliest start, latest end. Hidden when the day
  // is empty (no honest answer to "from when to when").
  const rangeLabel = (() => {
    if (todays.length === 0) return null;
    // Span of viewing times for the day — earliest to latest start (viewings
    // have no fixed end). A single viewing shows just its start.
    const starts = [...todays]
      .map((a) => new Date(a.scheduledAt).getTime())
      .sort((a, b) => a - b);
    const firstStr = formatTime(new Date(starts[0]!).toISOString());
    const lastStr = formatTime(
      new Date(starts[starts.length - 1]!).toISOString(),
    );
    return firstStr === lastStr ? firstStr : `${firstStr} — ${lastStr}`;
  })();

  return (
    <>
      {/* Day summary */}
      <div className="flex items-center justify-between text-[12px]">
        <span className="font-semibold text-foreground">
          {summaryLabel} <span className="text-ink-faint">·</span>{" "}
          <span className="text-foreground">{todays.length} lịch</span>
        </span>
        {rangeLabel !== null ? (
          <span className="font-mono text-muted-foreground">{rangeLabel}</span>
        ) : null}
      </div>

      {/* Card list */}
      {todays.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-[14px] text-muted-foreground">
          Không có lịch hẹn cho ngày này.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {todays.map((appointment) => (
            <MobileAppointmentCard
              key={appointment.id}
              appointment={appointment}
              now={now}
              leads={leads}
              brokers={brokers}
              onClick={() => onSelectAppointment(appointment)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

interface WeekViewProps {
  weekDays: Date[];
  byDate: Map<string, Appointment[]>;
  now: number;
  leads: Lead[];
  brokers: BrokerSummary[];
  onDateChange: (next: Date) => void;
  onSelectAppointment: (appointment: Appointment) => void;
}

function WeekView({
  weekDays,
  byDate,
  now,
  leads,
  brokers,
  onDateChange,
  onSelectAppointment,
}: WeekViewProps) {
  // Days that actually have appointments — skip the empty ones in the
  // week view so the user isn't scrolling past five "Không có lịch hẹn"
  // banners to reach the one busy day. Tapping a day header still jumps
  // the strip's selection so the user can drill in.
  const populated = weekDays.filter(
    (d) => (byDate.get(dateKey(d)) ?? []).length > 0,
  );

  if (populated.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-[14px] text-muted-foreground">
        Không có lịch hẹn trong tuần này.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {populated.map((day) => {
        const items = byDate.get(dateKey(day)) ?? [];
        const todayFlag = isToday(day);
        return (
          <section key={day.toISOString()} className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onDateChange(day)}
              className="flex items-baseline gap-2 text-left"
            >
              <span className="text-[14px] font-bold text-foreground">
                {todayFlag
                  ? "Hôm nay"
                  : `${["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"][day.getDay()]}`}
              </span>
              <span className="font-mono text-[12px] text-muted-foreground">
                {pad2(day.getDate())}/{pad2(day.getMonth() + 1)} ·{" "}
                {items.length} lịch
              </span>
            </button>
            <ul className="flex flex-col gap-3">
              {items.map((appointment) => (
                <MobileAppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  now={now}
                  leads={leads}
                  brokers={brokers}
                  onClick={() => onSelectAppointment(appointment)}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface MobileAppointmentCardProps {
  appointment: Appointment;
  now: number;
  leads: Lead[];
  brokers: BrokerSummary[];
  onClick: () => void;
}

function MobileAppointmentCard({
  appointment,
  now,
  leads,
  brokers,
  onClick,
}: MobileAppointmentCardProps) {
  const start = new Date(appointment.scheduledAt).getTime();
  const end = start + NOMINAL_VIEWING_MINUTES * 60_000;
  const isLive =
    appointment.status === "SCHEDULED" && now >= start && now < end;

  const lead = appointment.leadId !== null
    ? leads.find((l) => l.id === appointment.leadId)
    : undefined;
  const broker = brokers.find((b) => b.id === appointment.brokerId);

  // Title — defaults to "Xem nhà — {building}" since that's the
  // overwhelmingly common appointment type today and the design's
  // primary card layout. Falls back to bare "Lịch hẹn" when there's
  // no building reference to anchor against.
  const buildingName = lead?.interestedPropertyBuildingName ?? null;
  const title = buildingName !== null
    ? `Xem nhà — ${buildingName}`
    : "Lịch hẹn";

  const code = lead?.interestedPropertyInternalCode ?? null;
  const customerName = lead?.customer.fullName ?? null;
  const subtitle = [code, customerName].filter((s) => s !== null).join(" · ");

  return (
    <li className="flex items-start gap-3">
      {/* Time column — start (bold) over end (smaller). Fixed-width so
          the cards align vertically regardless of time-digit count. */}
      <div className="flex w-14 shrink-0 flex-col pt-2 font-mono leading-tight tabular-nums">
        <span className="text-[14px] font-bold">
          {formatTime(appointment.scheduledAt)}
        </span>
        <span className="text-[12px] text-muted-foreground">
          {formatTime(new Date(end).toISOString())}
        </span>
      </div>

      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex flex-1 flex-col gap-2 rounded-xl border bg-card p-3 text-left shadow-[var(--shadow-soft)] transition-colors hover:bg-muted/40",
          isLive
            ? "border-primary bg-primary/5 ring-1 ring-primary/30"
            : "border-border",
        )}
      >
        {/* Title row — "ĐANG" tag floats top-right when the card is
            currently in progress. */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-[14px] font-semibold leading-tight">{title}</h3>
          {isLive ? (
            <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[11px] font-bold tracking-wider text-primary uppercase">
              <span
                aria-hidden="true"
                className="size-1.5 animate-pulse rounded-full bg-primary"
              />
              Đang
            </span>
          ) : null}
        </div>

        {/* Location / customer line — only render when we have at
            least one piece to show. */}
        {subtitle.length > 0 ? (
          <p className="flex items-center gap-1.5 truncate text-[12px] text-muted-foreground">
            <MapPin aria-hidden="true" className="size-3.5 shrink-0" />
            <span className="truncate">{subtitle}</span>
          </p>
        ) : null}

        {/* Pills row — status pill (status-toned) + broker pill (neutral). */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
              STATUS_PILL_TONE[appointment.status],
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "size-1.5 rounded-full",
                STATUS_DOT_TONE[appointment.status],
              )}
            />
            {/* Mobile uses the friendlier label set; keep the
                APPOINTMENT_STATUS_LABELS export as the fallback so
                copy edits propagate if the mapping is later removed. */}
            {STATUS_LABEL_MOBILE[appointment.status] ??
              APPOINTMENT_STATUS_LABELS[appointment.status]}
          </span>
          {broker !== undefined ? (
            <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {broker.fullName}
            </span>
          ) : null}
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

interface ToggleButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function ToggleButton({ label, active, onClick }: ToggleButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors",
        active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

